import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { Check } from 'lucide-react';

import { useSeasonStore } from '@/stores/season-store';
import { SeasonType } from '@/types/season';
import type { Season } from '@/types/season';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { DateField } from '@/components/forms/DateField';
import { Button } from '@/components/common/Button';
import { STATE_NAME_OPTIONS } from '@/utils/us-states';
import { getSeasonDates, getStateRegulationMeta } from '@/data/season-regulations';
import type { SeasonRegulation } from '@/data/season-regulations';
import { isRegulationDataStale, SEASON_TYPE_LABELS } from '@/utils/season-utils';

const SEASON_TYPE_OPTIONS = [
  { value: SeasonType.Gun, label: 'Gun' },
  { value: SeasonType.Bow, label: 'Bow' },
  { value: SeasonType.Muzzleloader, label: 'Muzzleloader' },
  { value: SeasonType.Crossbow, label: 'Crossbow' },
  { value: SeasonType.Youth, label: 'Youth' },
  { value: SeasonType.Other, label: 'Other' },
];

const STATE_SELECT_OPTIONS = [
  { value: '', label: 'Select a state...' },
  ...STATE_NAME_OPTIONS,
];

const SEASON_TYPE_SELECT_OPTIONS = [
  { value: '', label: 'Select season type...' },
  ...SEASON_TYPE_OPTIONS,
];

function buildAutoName(state: string, seasonType: string, regulationName?: string): string {
  if (!state || !seasonType) return '';
  if (regulationName) return `${new Date().getFullYear()} ${state} ${regulationName}`;
  // seasonType comes from a <Select> whose options are SeasonType values, so the
  // assertion is safe; ?? '' guards the impossible miss without throwing.
  return SEASON_TYPE_LABELS[seasonType as SeasonType] ?? '';
}

/** Format an ISO date string as "Nov 21" for display in the season picker. */
function formatShortDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}


interface FormErrors {
  name?: string;
  state?: string;
  seasonType?: string;
  openerDate?: string;
  closerDate?: string;
}

interface SeasonFormProps {
  existing?: Season;
  onSubmit: (data: {
    name: string;
    state: string;
    seasonType: SeasonType;
    openerDate: string;
    closerDate?: string;
  }) => Promise<void>;
  backTo: string;
  isEdit: boolean;
}

function SeasonForm({ existing, onSubmit, backTo, isEdit }: SeasonFormProps) {
  const [name, setName] = useState(existing?.name ?? '');
  const [state, setState] = useState(existing?.state ?? '');
  const [seasonType, setSeasonType] = useState(existing?.seasonType ?? '');
  const [openerDate, setOpenerDate] = useState(existing?.openerDate ?? '');
  const [closerDate, setCloserDate] = useState(existing?.closerDate ?? '');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-population state
  // undefined = not yet looked up (state or season type not set)
  // null       = looked up but no data found for this state/season type/year
  // array      = regulations found (length >= 1)
  const [foundRegulations, setFoundRegulations] = useState<SeasonRegulation[] | null | undefined>(
    undefined,
  );
  const [selectedRegIdx, setSelectedRegIdx] = useState(-1);

  const lastAutoName = useRef(existing?.name ?? '');
  const CURRENT_YEAR = new Date().getFullYear();

  /** Apply a regulation: fill opener/closer and conditionally update the name. */
  function applyRegulation(reg: SeasonRegulation, stateVal: string, seasonTypeVal: string): void {
    setOpenerDate(reg.openerDate);
    setCloserDate(reg.closerDate);
    setErrors((e) => ({ ...e, openerDate: undefined, closerDate: undefined }));
    const autoName = buildAutoName(stateVal, seasonTypeVal, reg.name);
    if (!name || name === lastAutoName.current) {
      setName(autoName);
      lastAutoName.current = autoName;
    }
  }

  /**
   * Perform the regulation lookup for the given state + season type, update state,
   * and auto-apply if exactly one result is found.
   * Also handles the fallback auto-name when no regulations exist.
   */
  function lookupAndApply(stateVal: string, seasonTypeVal: string): void {
    if (!stateVal || !seasonTypeVal) {
      setFoundRegulations(undefined);
      setSelectedRegIdx(-1);
      // Auto-name with whatever partial values we have
      const auto = buildAutoName(stateVal, seasonTypeVal);
      if (!name || name === lastAutoName.current) {
        setName(auto);
        lastAutoName.current = auto;
      }
      return;
    }

    const regs = getSeasonDates(stateVal, seasonTypeVal as SeasonType, CURRENT_YEAR);
    setFoundRegulations(regs);

    if (regs && regs.length === 1) {
      // Exactly one match — auto-select and fill
      setSelectedRegIdx(0);
      applyRegulation(regs[0], stateVal, seasonTypeVal);
    } else if (regs && regs.length > 1) {
      // Multiple matches — show picker, don't auto-select
      setSelectedRegIdx(-1);
      const auto = buildAutoName(stateVal, seasonTypeVal);
      if (!name || name === lastAutoName.current) {
        setName(auto);
        lastAutoName.current = auto;
      }
    } else {
      // null — not supported; update auto-name but leave dates unchanged
      setSelectedRegIdx(-1);
      const auto = buildAutoName(stateVal, seasonTypeVal);
      if (!name || name === lastAutoName.current) {
        setName(auto);
        lastAutoName.current = auto;
      }
    }
  }

  function handleStateChange(value: string) {
    setState(value);
    lookupAndApply(value, seasonType);
  }

  function handleSeasonTypeChange(value: string) {
    setSeasonType(value);
    lookupAndApply(state, value);
  }

  function handleRegulationSelect(idx: number) {
    setSelectedRegIdx(idx);
    applyRegulation(foundRegulations![idx], state, seasonType);
  }

  function handleNameChange(value: string) {
    setName(value);
    if (!value) lastAutoName.current = '';
  }

  function validate(): FormErrors {
    const errs: FormErrors = {};
    if (!name.trim()) errs.name = 'Season name is required';
    if (!state) errs.state = 'State is required';
    if (!seasonType) errs.seasonType = 'Season type is required';
    if (!openerDate) errs.openerDate = 'Opener date is required';
    if (closerDate && openerDate && closerDate <= openerDate) {
      errs.closerDate = 'Closer date must be after opener date';
    }
    return errs;
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        state,
        seasonType: seasonType as SeasonType,
        openerDate,
        ...(closerDate ? { closerDate } : {}),
      });
    } catch {
      setIsSubmitting(false);
    }
  }

  // Derived display state
  const stateMeta = state ? getStateRegulationMeta(state) : null;
  const stale = stateMeta ? isRegulationDataStale(stateMeta.lastUpdated) : false;
  const autoFillActive = foundRegulations != null && selectedRegIdx >= 0;
  const noDataAvailable = foundRegulations === null && Boolean(state) && Boolean(seasonType);
  const showPicker = foundRegulations != null && foundRegulations.length > 1;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Link
          to={backTo}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
        <h1 className="font-display text-display-lg text-[var(--color-text-display)]">
          {isEdit ? 'Edit Season' : 'New Season'}
        </h1>
      </div>

      {/* hypothesis (fallback artifacts) — verify on device: min-w-0 w-full propagates flex shrink to DateField wrapper on iOS Safari PWA (L-01) */}
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5 min-w-0 w-full">
        <Select
          id="season-state"
          label="State"
          value={state}
          onChange={handleStateChange}
          options={STATE_SELECT_OPTIONS}
          required
          error={errors.state}
          data-testid="season-form-state"
        />

        <Select
          id="season-weapon"
          label="Season Type"
          value={seasonType}
          onChange={handleSeasonTypeChange}
          options={SEASON_TYPE_SELECT_OPTIONS}
          required
          error={errors.seasonType}
        />

        {/* Season picker — shown when multiple regulations match state + season type */}
        {showPicker && (
          <div className="flex flex-col gap-2" role="radiogroup" aria-label="Select season" data-testid="regulation-picker">
            <p className="text-body-sm font-medium text-[var(--color-text-primary)]">
              Select season
            </p>
            {foundRegulations!.map((reg, idx) => (
              <button
                key={`${reg.name}-${idx}`}
                type="button"
                role="radio"
                aria-checked={selectedRegIdx === idx}
                data-testid={`regulation-option-${idx}`}
                onClick={() => handleRegulationSelect(idx)}
                className={[
                  'flex items-center justify-between w-full px-4 py-3 rounded-lg border text-left transition-colors',
                  selectedRegIdx === idx
                    ? 'border-[var(--color-accent)] bg-[color-mix(in_srgb,var(--color-accent)_8%,transparent)]'
                    : 'border-[var(--color-border)] hover:border-[var(--color-border-focus)]',
                ].join(' ')}
              >
                <span className="font-medium text-body-sm text-[var(--color-text-primary)]">
                  {reg.name}
                </span>
                <span className="text-body-xs text-[var(--color-text-muted)] shrink-0 ml-3">
                  {formatShortDate(reg.openerDate)} – {formatShortDate(reg.closerDate)}
                </span>
              </button>
            ))}
          </div>
        )}

        <Input
          id="season-name"
          label="Season Name"
          value={name}
          onChange={handleNameChange}
          placeholder="e.g. 2026 WI Gun Deer"
          required
          error={errors.name}
          data-testid="season-form-name"
        />

        <DateField
          id="season-opener"
          label="Opener Date"
          value={openerDate}
          onChange={(v) => {
            setOpenerDate(v);
            if (errors.openerDate) setErrors((e) => ({ ...e, openerDate: undefined }));
          }}
          required
        />
        {errors.openerDate && (
          <p className="text-body-xs text-[var(--color-accent)] -mt-3">{errors.openerDate}</p>
        )}

        <DateField
          id="season-closer"
          label="Closer Date"
          value={closerDate}
          onChange={(v) => {
            setCloserDate(v);
            if (errors.closerDate) setErrors((e) => ({ ...e, closerDate: undefined }));
          }}
        />
        {errors.closerDate && (
          <p className="text-body-xs text-[var(--color-accent)] -mt-3">{errors.closerDate}</p>
        )}

        {/* Regulatory badge — shown when a regulation was auto-applied */}
        {autoFillActive && (
          <span
            data-testid="regulatory-badge"
            className="inline-flex items-center gap-1.5 bg-[#4a8c5c]/10 text-[#4a8c5c] border-[0.5px] border-[#4a8c5c]/30 rounded-sm px-2 py-0.5 text-body-xs"
          >
            <Check className="w-3 h-3" aria-hidden="true" />
            REGULATORY DATA APPLIED
          </span>
        )}

        {/* No-data fallback note — shown when state+season type are set but no data exists */}
        {noDataAvailable && (
          <p className="text-body-xs text-[var(--color-text-muted)]" data-testid="no-data-note">
            {CURRENT_YEAR} {state} season dates not available — enter manually.
            {stateMeta && (
              <>
                {' '}
                <a
                  href={stateMeta.dnrUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-[var(--color-text-primary)] transition-colors"
                >
                  {state} DNR ↗
                </a>
              </>
            )}
          </p>
        )}

        {/* Stale data warning — shown when this state's data is >6 months old */}
        {stale && stateMeta && (
          <p className="text-body-xs text-[var(--color-warning,#d97706)]" data-testid="stale-data-warning">
            Season dates may have changed — verify with your state DNR before finalizing.{' '}
            <a
              href={stateMeta.dnrUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              {state} DNR ↗
            </a>
          </p>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="submit" variant="primary" disabled={isSubmitting} data-testid="season-form-submit">
            {isSubmitting
              ? isEdit ? 'Saving...' : 'Creating...'
              : isEdit ? 'Save Changes' : 'Create Season'}
          </Button>
          <Link to={backTo}>
            <Button type="button" variant="ghost" disabled={isSubmitting}>
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}

export function SeasonFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(id);

  const { getSeason, addSeason, editSeason, loadSeasons, seasons, isLoading } = useSeasonStore();

  // Trigger a load if the store is empty (direct URL navigation to edit page)
  useEffect(() => {
    if (seasons.length === 0) void loadSeasons();
  }, [seasons.length, loadSeasons]);

  const existing = id ? getSeason(id) : undefined;
  const backTo = isEdit && id ? `/seasons/${id}` : '/seasons';

  // In edit mode, wait until the season is available
  if (isEdit && !existing) {
    if (isLoading || seasons.length === 0) {
      return (
        <div className="flex items-center justify-center py-16">
          <p className="text-[var(--color-text-muted)] text-body-sm">Loading...</p>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-4 py-8">
        <p className="text-[var(--color-text-muted)] text-body-sm">Season not found.</p>
        <Link to="/seasons"><Button variant="ghost">Back to Seasons</Button></Link>
      </div>
    );
  }

  async function handleSubmit(data: {
    name: string;
    state: string;
    seasonType: SeasonType;
    openerDate: string;
    closerDate?: string;
  }) {
    const fromOnboarding = (location.state as { fromOnboarding?: boolean } | null)?.fromOnboarding === true;
    if (isEdit && id) {
      await editSeason(id, data);
      navigate(`/seasons/${id}`);
    } else {
      const season = await addSeason(data);
      if (fromOnboarding) {
        navigate('/', { replace: true, state: { toastMessage: 'Season defined. Countdown active.' } });
      } else {
        navigate(`/seasons/${season.id}`);
      }
    }
  }

  return (
    // key ensures form re-initializes with fresh values if season id changes
    <SeasonForm
      key={id ?? 'new'}
      existing={existing}
      onSubmit={handleSubmit}
      backTo={backTo}
      isEdit={isEdit}
    />
  );
}
